import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useSearchProcedurePreviewQuery } from 'presentation/pages/private/refund/pages/RequestPreview/queries/useSearchProcedurePreviewQuery';
import { translations } from 'presentation/translations-new';
import { useGetBeneficiaries } from '../../../../Request/hooks/useGetBeneficiaries';
import { useFinishPreview } from './useFinishPreview';
import { SelectTypeOptionLabels } from '../../SelectTypeV2/types';
import { useRequestPreviewDataStore } from '../../../store/useRequestPreviewDataStore';
import { useRequestPreviewFlowStore } from '../../../store/useRequestPreviewFlowStore';
import { useStepPreviewStore } from '../../../store/useStepPreviewStore';
import { StepPreviewEnum } from '../../../types';
import { getPreviewRequestFormSchema, PreviewRequestFormSchemaValues } from '../schema';

const DISPLAYED_PAGE_SIZE = 3;

const proceduresFormValidation =
	translations['pt-br'].privateFlow.refund.previewServiceType.proceduresForm.validation;

export function useProcedures(handleNextStep?: () => void) {
	const setStep = useStepPreviewStore(state => state.setStep);
	const setUseServiceTypeFormV2 = useRequestPreviewFlowStore(
		state => state.setUseServiceTypeFormV2,
	);
	const requestPreviewState = useRequestPreviewDataStore(state => state.data);
	const setRequestPreviewDataState = useRequestPreviewDataStore(state => state.setData);
	const selectedServiceTypeOption = useRequestPreviewFlowStore(
		state => state.selectedServiceTypeOption,
	);
	const isConsultation =
		selectedServiceTypeOption === SelectTypeOptionLabels.CONSULTA;
	const isTherapies =
		selectedServiceTypeOption === SelectTypeOptionLabels.TERAPIAS;
	const [visibleCount, setVisibleCount] = useState(DISPLAYED_PAGE_SIZE);
	const [appliedFilterTerm, setAppliedFilterTerm] = useState('');

	const beneficiaryUserCode = requestPreviewState?.beneficiary?.userCode ?? '';

	const {
		data: procedureSearchData,
		isLoading: isProcedureSearchLoading,
		isError: isProcedureListError,
		refetch: refetchProcedures,
	} = useSearchProcedurePreviewQuery({
		beneficiaryUserCode,
	});

	const fullList = procedureSearchData?.options ?? [];
	const hasProcedureResults =
		appliedFilterTerm.trim() !== '' &&
		fullList.some(
			opt =>
				opt.label.toLowerCase().includes(appliedFilterTerm.trim().toLowerCase()) ||
				opt.code.toLowerCase().includes(appliedFilterTerm.trim().toLowerCase()),
		);

	const formMethods = useForm<PreviewRequestFormSchemaValues>({
		resolver: zodResolver(
			getPreviewRequestFormSchema(
				isConsultation,
				hasProcedureResults,
				proceduresFormValidation,
			),
		),
		mode: 'onSubmit',
		defaultValues: {
			beneficiary: requestPreviewState?.beneficiary?.userCode
				? requestPreviewState.beneficiary
				: null,
			procedureSearch: '',
			selectedProcedure: null,
		},
	});

	const {
		control,
		handleSubmit,
		getValues,
		setValue,
		setError,
		clearErrors,
		formState: { errors },
	} = formMethods;

	const searchResults = useMemo(() => {
		const term = appliedFilterTerm.trim().toLowerCase();
		if (!term) return [];
		return fullList.filter(
			opt =>
				opt.label.toLowerCase().includes(term) ||
				opt.code.toLowerCase().includes(term),
		);
	}, [fullList, appliedFilterTerm]);

	const displayedResults = useMemo(
		() => searchResults.slice(0, visibleCount),
		[searchResults, visibleCount],
	);

	const hasNextPage = visibleCount < searchResults.length;
	const isSearching =
		isProcedureSearchLoading && appliedFilterTerm.trim() !== '';

	const showEmptySearch =
		!isSearching &&
		fullList.length > 0 &&
		appliedFilterTerm.trim() !== '' &&
		searchResults.length === 0;

	useEffect(() => {
		setVisibleCount(DISPLAYED_PAGE_SIZE);
	}, [searchResults.length]);

	const {
		handleFinishPreview,
		handleResetErrors,
		handleResetValueZero,
		isLoading: isFinishLoading,
		hasError: hasFinishError,
		isValueZero,
	} = useFinishPreview(handleNextStep);

	const {
		loadingList: beneficiaryLoadingList,
		options: beneficiaryOptions,
		open: beneficiaryOpen,
		handleOpen: handleBeneficiaryOpen,
		handleClose: handleBeneficiaryClose,
	} = useGetBeneficiaries();

	function onSearch() {
		const beneficiary = getValues('beneficiary');
		const procedureSearch = getValues('procedureSearch')?.trim() ?? '';

		if (!beneficiary?.userCode) {
			setError('beneficiary', {
				message: proceduresFormValidation.selectPatient,
			});
			return;
		}

		if (procedureSearch.length < 2) {
			setError('procedureSearch', {
				message: proceduresFormValidation.minSearchLength,
			});
			return;
		}

		clearErrors(['beneficiary', 'procedureSearch', 'selectedProcedure']);
		setValue('selectedProcedure', null);
		setAppliedFilterTerm(procedureSearch);
	}

	function handleNotFoundProcedure() {
		setUseServiceTypeFormV2(false);
		setStep(StepPreviewEnum.SERVICE_TYPE);
	}

	function clearProcedureFields() {
		setValue('procedureSearch', '');
		setValue('selectedProcedure', null);
		setAppliedFilterTerm('');
		setVisibleCount(DISPLAYED_PAGE_SIZE);
	}

	const hasNextPageRef = useRef(hasNextPage);
	hasNextPageRef.current = hasNextPage;

	const loadMoreProcedures = useCallback(() => {
		if (hasNextPageRef.current) {
			setVisibleCount(c => c + DISPLAYED_PAGE_SIZE);
		}
	}, []);

	return {
		formMethods,
		control,
		handleSubmit,
		errors,
		searchResults,
		displayedResults,
		isSearching,
		onSearch,
		proceduresFormValidation,
		handleNotFoundProcedure,
		beneficiaryLoadingList,
		beneficiaryOptions,
		beneficiaryOpen,
		handleBeneficiaryOpen,
		handleBeneficiaryClose,
		requestPreviewState,
		setRequestPreviewDataState,
		clearProcedureFields,
		loadMoreProcedures,
		hasNextPage,
		isFetchingNextPage: false,
		showEmptySearch,
		isConsultation,
		isTherapies,
		isFinishLoading,
		isProcedureListError,
		refetchProcedures,
		hasFinishError,
		handleResetErrors,
		handleFinishPreview,
		isValueZero,
		handleResetValueZero,
	};
}
